import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import './Profile.css';

const API_BASE = (process.env.REACT_APP_API_URL || "https://ukd45lve3kvj45tahklem6d67q0fbbxm.lambda-url.us-west-2.on.aws/api/chat").replace('/api/chat', '');

const LEVEL_LABELS = {
    mostly_gone: 'Mostly gone',
    in_progress: 'In progress',
    just_starting: 'Just starting',
};

const LEVEL_WIDTH = {
    mostly_gone: '85%',
    in_progress: '50%',
    just_starting: '20%',
};

export default function ProfilePage() {
    const auth = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const token = await auth?.getIdToken();
                if (!token) return;
                const res = await fetch(`${API_BASE}/api/profile/snapshot`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                    const data = await res.json();
                    setProfile(data.profile);
                } else {
                    const data = await res.json().catch(() => ({}));
                    setError(data.reason || data.error || 'Failed to load profile.');
                }
            } catch (e) {
                setError('Could not reach server.');
            } finally {
                setLoading(false);
            }
        })();
    }, [auth]);

    return (
        <div className="profile-app">
            <header className="profile-topbar">
                <div className="profile-topbar-left">
                    <button className="profile-back" onClick={() => navigate('/')}>
                        <svg viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                    </button>
                    <div className="profile-topbar-brand">Profile</div>
                </div>
            </header>

            <div className="profile-content">
                {loading ? (
                    <div className="profile-loading">Generating your profile...</div>
                ) : error ? (
                    <div className="profile-empty">
                        <h2>No profile yet</h2>
                        <p>{error}</p>
                        <button className="profile-cta" onClick={() => navigate('/')}>Start chatting</button>
                    </div>
                ) : !profile ? (
                    <div className="profile-empty">
                        <h2>Not enough data yet</h2>
                        <p>Keep chatting and sharing field reports. Your profile will generate once there's enough to work with.</p>
                        <button className="profile-cta" onClick={() => navigate('/')}>Back to chat</button>
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div className="profile-header">
                            <div className="profile-header-stat">
                                <span className="profile-header-num">{profile.header?.field_report_count || 0}</span>
                                <span className="profile-header-label">field reports</span>
                            </div>
                            <div className="profile-header-stat">
                                <span className="profile-header-num">{profile.header?.weeks_since_joining || 0}</span>
                                <span className="profile-header-label">weeks</span>
                            </div>
                        </div>

                        {/* Where you are */}
                        {profile.where_you_are && (
                            <section className="profile-section narrative">
                                <h3>Where you are</h3>
                                <p>{profile.where_you_are}</p>
                            </section>
                        )}

                        {/* What's working */}
                        {profile.whats_working && profile.whats_working.length > 0 && (
                            <section className="profile-section">
                                <h3>What's working</h3>
                                <div className="profile-tags">
                                    {profile.whats_working.map((tag, i) => (
                                        <span key={i} className="profile-tag">{tag}</span>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Sticking points */}
                        {profile.sticking_points && profile.sticking_points.length > 0 && (
                            <section className="profile-section">
                                <h3>Current sticking points</h3>
                                <div className="profile-cards">
                                    {profile.sticking_points.map((sp, i) => (
                                        <div key={i} className="profile-card sticking">
                                            <div className="profile-card-header">
                                                <span className="profile-card-title">{sp.title}</span>
                                                <span className="profile-card-freq">{sp.frequency}x</span>
                                            </div>
                                            <p>{sp.description}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Progression */}
                        {profile.progression && profile.progression.length > 0 && (
                            <section className="profile-section">
                                <h3>Progression</h3>
                                <div className="profile-progression">
                                    {profile.progression.map((p, i) => (
                                        <div key={i} className="profile-progress-row">
                                            <span className="profile-progress-skill">{p.skill}</span>
                                            <div className="profile-progress-bar">
                                                <div
                                                    className={`profile-progress-fill ${p.level}`}
                                                    style={{ width: LEVEL_WIDTH[p.level] || '50%' }}
                                                />
                                            </div>
                                            <span className="profile-progress-label">{LEVEL_LABELS[p.level] || p.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Context */}
                        {profile.context && (
                            <section className="profile-section">
                                <h3>Context</h3>
                                <div className="profile-cards context">
                                    {profile.context.dating_context && (
                                        <div className="profile-card context">
                                            <span className="profile-card-label">Dating context</span>
                                            <span className="profile-card-value">{profile.context.dating_context}</span>
                                        </div>
                                    )}
                                    {profile.context.goal && (
                                        <div className="profile-card context">
                                            <span className="profile-card-label">Goal</span>
                                            <span className="profile-card-value">{profile.context.goal}</span>
                                        </div>
                                    )}
                                    {profile.context.most_active_environment && (
                                        <div className="profile-card context">
                                            <span className="profile-card-label">Most active</span>
                                            <span className="profile-card-value">{profile.context.most_active_environment}</span>
                                        </div>
                                    )}
                                    {profile.context.member_since && (
                                        <div className="profile-card context">
                                            <span className="profile-card-label">Member since</span>
                                            <span className="profile-card-value">{profile.context.member_since}</span>
                                        </div>
                                    )}
                                </div>
                            </section>
                        )}

                        {/* Focus next */}
                        {profile.focus_next && (
                            <section className="profile-section narrative focus">
                                <h3>What to focus on next</h3>
                                <p>{profile.focus_next}</p>
                                <button className="profile-link" onClick={() => navigate('/actions')}>
                                    View your actions
                                </button>
                            </section>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
